import { WuiIcon, IconName } from '@wui/common-library'
import {
  AnyConferenceChangedByOtherEvent,
  AnyConferenceChangedByYouEvent,
  CallPastMessage,
  HistoryMessageItem,
  IMFile,
  MessageIncomingEvent,
  MessageOutgoingEvent,
  MessageStatusType,
  ReactionAddedByOtherEvent,
  ReactionAddedByYouEvent,
  MessagePinnedByOtherEvent,
  isMessageDeleted,
  MessageCallEnded,
} from '@wui/im'
import { ComponentOptions, h, PropType, VNode, VNodeArrayChildren } from 'vue'

import { getCallEventText } from '@/entities/call-chat-event'
import { getSystemMessageText } from '@/entities/chat-system-message'
import { useStoreContacts } from '@/entities/contact'
import {
  chatModel,
  draftModel,
  getPinnedEventText,
  getPinnedSystemQuote,
  truncatePinnedSystemPreview,
  usePinEventsStore,
} from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'
import { isVoiceMessage, useVoiceMessageStore } from '@/entities/voice-message'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { bytesConverter } from '@/shared/utils/bytes-converter'
import { getFileTypeByName, IMAGE_TYPE } from '@/shared/utils/files'
import { sanitizeToText } from '@/shared/utils/sanitize'
import { getAuthenticatedMediaUrl } from '@/shared/utils/url'
import { isVCardFile } from '@/shared/utils/vcard'

interface Props extends ComponentOptions {
  event?: HistoryMessageItem,
  draft?: draftModel.DraftMessage
  pServed?: string
}

const props = {
  event: {
    type: Object as PropType<HistoryMessageItem>,
    required: false,
  },
  draft: {
    type: Object as PropType<draftModel.DraftMessage>,
    required: false,
  },
  pServed: {
    type: String,
    required: false,
    default: '',
  },
}

/** surf/fg/base/fg17 */
const PREVIEW_FG_CLASS = 'text-fg-base-fg17'
/** surf/brand/baseDarkLighter/def — в kit: basedarkblue */
const SYSTEM_PREVIEW_CLASS = 'text-surf-brand-basedarkblue-def'
const CALL_MISSED_CLASS = 'text-surf-neg-base-def'
const CALL_ANSWERED_CLASS = 'text-surf-pos-base-def'

const MISSED_CALL_EVENTS = new Set([
  'call_missed',
  'dialing_terminated',
  'dialing_busy',
  'dialing_rejected',
])

const isPreviewVideoFile = (filename: string) => {
  const type = getFileTypeByName(filename)
  return type === 'videoFile' || type === 'unplayableVideoFile'
}

const MAX_IMAGE_THUMBNAILS = 3

const getAttachmentIcon = (file: IMFile | undefined): IconName => {
  if (!file) return 'fileM'
  if (isVCardFile(file)) return 'userRoundM'
  if (isPreviewVideoFile(file.filename ?? '')) return 'videoM'
  return 'fileM'
}

const isImageAttachment = (file: IMFile) => getFileTypeByName(file.filename ?? '') === IMAGE_TYPE

const renderImageThumbnails = (images: IMFile[]): VNode[] =>
  images.slice(0, MAX_IMAGE_THUMBNAILS).map((file) => h('img', {
    key: file.filecookie,
    src: getAuthenticatedMediaUrl(`/files/${file.filecookie}`),
    class: 'h-4 w-4 shrink-0 rounded-2 object-cover',
  }))

const renderAttachmentContent = (files: IMFile[], messageText: string, isVoice: boolean): VNode => {
  const { t } = useLocalization()

  if (isVoice) {
    return h('div', { class: 'flex min-w-0 items-center' }, [
      h(WuiIcon, { name: 'micM', small: true, class: `${PREVIEW_FG_CLASS} mr-1 shrink-0` }),
      h('span', { class: `truncate ${PREVIEW_FG_CLASS}` }, t('mainAside.voiceMessage')),
    ])
  }

  const images = files.filter(isImageAttachment)
  const isImagesOnly = files.length > 0 && images.length === files.length

  if (isImagesOnly) {
    const first = images[0]
    const fallbackLabel = files.length === 1
      ? `${first?.filename ?? ''} ${bytesConverter(first?.filesize)}`.trim()
      : t('common.images')

    return h('div', { class: 'flex min-w-0 items-center gap-x-1' }, [
      ...renderImageThumbnails(images),
      h('span', { class: `ml-1 truncate ${PREVIEW_FG_CLASS}` }, messageText || fallbackLabel),
    ])
  }

  const firstFile = files[0]
  const count = files.length
  const string = count > 1
    ? t('mainAside.files', { count })
    : `${firstFile?.filename ?? ''} ${bytesConverter(firstFile?.filesize)}`.trim()

  return h('div', { class: 'flex min-w-0 items-center' }, [
    h(WuiIcon, { name: getAttachmentIcon(firstFile), small: true, class: `${PREVIEW_FG_CLASS} mr-1 shrink-0` }),
    h('span', { class: `${PREVIEW_FG_CLASS} mr-1` }, string),
    h('span', { class: `truncate ${PREVIEW_FG_CLASS}` }, messageText),
  ])
}

const wrapWithForwardIcon = (content: VNode): VNode =>
  h('div', { class: 'flex min-w-0 items-center gap-x-1' }, [
    h(WuiIcon, {
      name: 'arrowResendM',
      small: true,
      class: `${PREVIEW_FG_CLASS} shrink-0`,
    }),
    content,
  ])

const wrapWithSendStatusIcon = (content: VNode, status?: MessageStatusType): VNode => {
  const { t } = useLocalization()

  if (status === MessageStatusType.INITIAL) {
    return h('div', {
      class: 'flex min-w-0 items-center gap-x-1',
      title: t('common.sending'),
    }, [
      h(WuiIcon, {
        name: 'clockM',
        small: true,
        class: `${PREVIEW_FG_CLASS} shrink-0`,
      }),
      content,
    ])
  }

  if (status === MessageStatusType.ERROR) {
    return h('div', {
      class: 'flex min-w-0 items-center gap-x-1',
      title: t('common.unsent'),
    }, [
      h(WuiIcon, {
        name: 'warningM',
        small: true,
        class: `${CALL_MISSED_CLASS} shrink-0`,
      }),
      content,
    ])
  }

  return content
}

const resolvePreviewFiles = (item: MessageIncomingEvent | MessageOutgoingEvent): IMFile[] => {
  if (item.files?.length) return item.files
  if (item.forwarding?.forwardingFiles?.length) return item.forwarding.forwardingFiles
  return []
}

const resolvePreviewText = (item: MessageIncomingEvent | MessageOutgoingEvent): string =>
  sanitizeToText(item.messageText || item.forwarding?.forwardingText || '')

const _NullPreview = ({ name }: Props): VNode => {
  const previewText = name || useLocalization().t('mainAside.emptyMessages')
  return h('span', { class: `truncate ${PREVIEW_FG_CLASS}` }, previewText)
}

_NullPreview.props = {
  ...props,
  name: {
    type: String,
    required: false,
    default: undefined,
  },
}
export const NullPreview = _NullPreview

const _DefaultPreview = ({ event }: Props): VNode => {
  const typedEvent = event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>

  const isDeleted = isMessageDeleted(typedEvent)

  const { t } = useLocalization()

  const messageText = typedEvent.item.messageText
  const previewText = isDeleted ? t('chat.messageRemoved') : messageText ?? ''

  let message: string | VNode = ''
  if (isDeleted) {
    message = h('i', previewText)
  } else {
    message = sanitizeToText(previewText)
  }

  return wrapWithSendStatusIcon(
    h('span', { class: `truncate ${PREVIEW_FG_CLASS}` }, message),
    typedEvent.status,
  )
}

_DefaultPreview.props = props
export const DefaultPreview = _DefaultPreview

const _CallPreview = ({ event }: Props): VNode => {
  const typedEvent = event as HistoryMessageItem<CallPastMessage>

  const { currentUser } = useCurrentUser()

  const isMissed = MISSED_CALL_EVENTS.has(typedEvent.item.event)
  let iconName: IconName = 'arrowSouthWestM'
  if ((typedEvent.item as MessageCallEnded).srcPServed === currentUser.value?.imLogin) {
    iconName = 'arrowNorthEastM'
  }
  if (typedEvent.item.event === 'dialing_terminated') {
    iconName = 'callCanceled'
  }
  if (typedEvent.item.event === 'call_missed') {
    iconName = 'callMissed'
  }

  const colorClass = isMissed ? CALL_MISSED_CLASS : CALL_ANSWERED_CLASS

  return h('span', { class: 'flex items-center' }, [
    h(WuiIcon, { name: iconName, small: true, class: `${colorClass} mr-1 shrink-0` }),
    h('span', { class: `truncate ${colorClass}` }, getCallEventText(typedEvent)),
  ])
}

_CallPreview.props = props
export const CallPreview = _CallPreview

const _DraftPreview = ({ draft, pServed }: Props): VNode | undefined => {
  const { t } = useLocalization()
  const voiceSession = pServed ? useVoiceMessageStore().getSession(pServed) : undefined

  if (voiceSession) {
    return h('span', { class: 'flex items-center gap-1' }, [
      h('span', { class: `${PREVIEW_FG_CLASS} font-light` }, t('mainAside.draftWithMessage')),
      h('span', { class: `truncate ${PREVIEW_FG_CLASS}` }, t('mainAside.voiceDraft')),
    ])
  }

  if (draft?.messageText) {
    const message = sanitizeToText(draft?.messageText ?? '')
    const cleanedString = message.replace(/&lt;/g, '<').replace(/&gt;/g, '>')

    return h('span', { class: 'flex items-center gap-1' }, [
      h('span', { class: `${PREVIEW_FG_CLASS} font-light` }, t('mainAside.draftWithMessage')),
      h('span', { class: `truncate ${PREVIEW_FG_CLASS}` }, cleanedString),
    ])
  }

  if (draft?.files && draft.files.length > 0) {
    const count = draft.files.length
    const firstFile = draft.files[0]?.file
    const isFirstFileIsImage = getFileTypeByName(firstFile?.name ?? '') === IMAGE_TYPE
    const key = isFirstFileIsImage ? t('mainAside.imageDraft', { size: bytesConverter(firstFile?.size), name: firstFile?.name }) : t('mainAside.fileDraft', { size: bytesConverter(firstFile?.size), name: firstFile?.name })
    const string = count > 1 ? t('mainAside.filesDraft', { count }) : key

    return h('span', { class: 'flex items-center gap-1', title: `${t('mainAside.draftWithMessage')} ${string}` }, [
      h('span', { class: `${PREVIEW_FG_CLASS} font-light` }, t('mainAside.draftWithMessage')),
      h('span', { class: `truncate ${PREVIEW_FG_CLASS}` }, string),
    ])
  }

  if (draft?.quotedEventId) {
    return h(
      'span',
      [
        h('span', { class: `${PREVIEW_FG_CLASS} font-light` }, t('mainAside.draftWithMessage')),
        h(WuiIcon, { name: 'formatQuote', class: `w-4! h-4! shrink-0 ${PREVIEW_FG_CLASS}!` }),
        h('span', { class: PREVIEW_FG_CLASS }, t('common.quotedMessage')),
      ],
    )
  }
  return
}

_DraftPreview.props = props
export const DraftPreview = _DraftPreview

const _FilesPreview = ({ event }: Props): VNode => {
  const typedEvent = event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
  const files = resolvePreviewFiles(typedEvent.item)
  const messageText = resolvePreviewText(typedEvent.item)

  return wrapWithSendStatusIcon(
    renderAttachmentContent(files, messageText, isVoiceMessage(typedEvent.item)),
    typedEvent.status,
  )
}
_FilesPreview.props = props
export const FilesPreview = _FilesPreview

const _ReactionPreview = ({ event }: Props): VNode => {
  const reactionEvent = event as unknown as HistoryMessageItem<ReactionAddedByOtherEvent | ReactionAddedByYouEvent>

  const { t } = useLocalization()

  if ('rank' in reactionEvent.item) { // Added by other
    const { findByPServed } = useStoreContacts()
    const author = findByPServed(reactionEvent.item.executor)?.name
    return h('span', { class: PREVIEW_FG_CLASS }, t('mainAside.otherReactionPreview', {
      author,
      reaction: reactionEvent.item.reactionType,
    }))
  } else {
    return h('span', { class: PREVIEW_FG_CLASS }, t('mainAside.myReactionPreview', { reaction: reactionEvent.item.reactionType }))
  }
}
_ReactionPreview.props = props
export const ReactionPreview = _ReactionPreview

const _ForwardMessagePreview = ({ event }: Props): VNode => {
  const { t } = useLocalization()
  const typedEvent = event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
  const files = resolvePreviewFiles(typedEvent.item)
  const messageText = resolvePreviewText(typedEvent.item)

  let content: VNode
  if (files.length || isVoiceMessage(typedEvent.item)) {
    content = renderAttachmentContent(files, messageText, isVoiceMessage(typedEvent.item))
  } else if (messageText) {
    content = h('span', { class: `truncate ${PREVIEW_FG_CLASS}` }, messageText)
  } else {
    content = h('span', { class: `truncate ${PREVIEW_FG_CLASS}` }, t('chat.forwardMessageIsReady'))
  }

  return wrapWithSendStatusIcon(wrapWithForwardIcon(content), typedEvent.status)
}
_ForwardMessagePreview.props = props
export const ForwardMessagePreview = _ForwardMessagePreview

export const ConferenceEditPreview = ({ event }: Props): VNode => {
  const typedMessage = event as HistoryMessageItem<AnyConferenceChangedByOtherEvent | AnyConferenceChangedByYouEvent>

  const { item: { type }} = typedMessage
  const children: VNodeArrayChildren = []
  if (type === 'ConferenceMemberAddedEvent') {
    children.push(h(WuiIcon, {
      name: 'addCircleOutlined',
      small: true,
      class: `mr-1 ${SYSTEM_PREVIEW_CLASS} shrink-0`,
    }))
  }
  if (type === 'ConferenceMemberRemoved') {
    children.push(h(WuiIcon, {
      name: 'remove',
      small: true,
      class: `mr-1 ${SYSTEM_PREVIEW_CLASS} shrink-0`,
    }))
  }
  if (type === 'ConferenceScheduleChanged') {
    children.push(h(WuiIcon, {
      name: 'calendarM',
      small: true,
      class: `mr-1 ${SYSTEM_PREVIEW_CLASS} shrink-0`,
    }))
  }
  if (type === 'ConferenceScheduleUpdated') {
    children.push(h(WuiIcon, {
      name: 'eventCalendar',
      small: true,
      class: `mr-1 ${SYSTEM_PREVIEW_CLASS} shrink-0`,
    }))
  }
  if (type === 'ConferenceScheduleDisabled') {
    children.push(h(WuiIcon, {
      name: 'calendarDeleteM',
      small: true,
      class: `mr-1 ${SYSTEM_PREVIEW_CLASS} shrink-0`,
    }))
  }

  children.push(h('span', { class: `truncate ${SYSTEM_PREVIEW_CLASS} first-letter:capitalize` }, getSystemMessageText(typedMessage)))

  return h('span', { class: 'flex items-center' }, children)
}

const getPinnedPreviewQuote = (event: HistoryMessageItem<MessagePinnedByOtherEvent>) => {
  const targetEventId = event.item.targetEventId
  const historyMessage = chatModel.useChatMessageStore()
    .findById<MessageIncomingEvent | MessageOutgoingEvent>(targetEventId)
  const pin = usePinEventsStore().getPinEventsForRoom(event.item.pServed)
    ?.find((entry) => entry.eventId === targetEventId)
  const quote = getPinnedSystemQuote(pin, historyMessage)
  return {
    text: truncatePinnedSystemPreview(quote.text),
    isMessageText: quote.isMessageText,
  }
}

const getPinnedPreviewExecutorName = (event: HistoryMessageItem<MessagePinnedByOtherEvent>): string => {
  const { t } = useLocalization()
  const executor = event.item.executor
  const contactName = useStoreContacts().findByPServed(executor)?.name
  if (contactName) {
    return contactName
  }

  const isGuest = Boolean(executor?.includes('<sip:GUEST'))
  if (!isGuest) {
    return ''
  }

  const guestName = useStoreRooms().findGuestByPserved(event.item.pServed, executor)
  return guestName ? `${guestName} (${t('common.guest')})` : t('common.guest')
}

export const PinMessagePreview = ({ event }: Props): VNode => {
  const typedEvent = event as HistoryMessageItem<MessagePinnedByOtherEvent>
  const { currentUser } = useCurrentUser()
  const isOwn = currentUser.value?.imLogin === typedEvent.item.executor
  const quote = getPinnedPreviewQuote(typedEvent)
  const hasPreviewText = Boolean(quote.text)
  const actionText = getPinnedEventText(typedEvent, { isOwn, hasPreviewText })
  const actionWithQuote = hasPreviewText ? `${actionText} «${quote.text}»` : actionText
  const executorName = isOwn ? '' : getPinnedPreviewExecutorName(typedEvent)
  const previewText = executorName ? `${executorName} ${actionWithQuote}` : actionWithQuote

  return h('span', { class: `truncate ${SYSTEM_PREVIEW_CLASS} first-letter:capitalize` }, previewText)
}
